import { useCallback, useState } from 'react';

type UpdateFunction<T extends object> = (value: T) => T;
type Update<T extends object> = T | UpdateFunction<T>;

function isFunction<T extends object>(
  value: Update<T>,
): value is (value: T) => T {
  return typeof value === 'function';
}

export function usePersistedState<T extends object>(
  key: string,
  defaultValue: T,
): [T, (value: Update<T>) => void] {
  const [state, setState] = useState<T>((): T => {
    const storedValue = localStorage.getItem(key);
    if (storedValue) {
      try {
        return JSON.parse(storedValue) as T;
      } catch {
        return defaultValue;
      }
    }
    return defaultValue;
  });

  const updateState = useCallback(
    (value: Update<T>) =>
      setState((oldValue) => {
        let newValue: T;
        if (isFunction(value)) {
          newValue = value(oldValue);
        } else {
          newValue = value;
        }
        localStorage.setItem(key, JSON.stringify(newValue));
        return newValue;
      }),
    [],
  );

  return [state, updateState];
}
